import React, { useEffect, useRef, useState } from 'react';
import { Copy, Check, Trash2, Filter, Wrench } from 'lucide-react';
import styles from './LivePreviewTerminal.module.css';
import stripAnsi from 'strip-ansi';

export type TerminalFilterMode = 'all' | 'errors' | 'warnings' | 'install' | 'activity';

interface LivePreviewTerminalProps {
  output: string[];
  filteredOutput?: string[];
  activityLog?: string[];
  filter?: TerminalFilterMode;
  onFilterChange?: (filter: TerminalFilterMode) => void;
  className?: string;
  onClear?: () => void;
  onTryFix?: (errorContext: string) => void;
}

const FILTER_OPTIONS: { value: TerminalFilterMode; label: string; title: string }[] = [
  { value: 'all', label: 'All', title: 'Show all terminal output' },
  { value: 'errors', label: 'Errors', title: 'Show only errors' },
  { value: 'warnings', label: 'Warn+', title: 'Show warnings and errors' },
  { value: 'install', label: 'Install', title: 'Show only installation issues' },
  { value: 'activity', label: 'AI Activity', title: 'Show AI agent activity — who is coding what' },
];

export const LivePreviewTerminal: React.FC<LivePreviewTerminalProps> = ({
  output,
  filteredOutput,
  activityLog = [],
  filter = 'all',
  onFilterChange,
  className,
  onClear,
  onTryFix,
}) => {
  // Use filtered output if provided and filter is not 'all', otherwise use raw output
  const displayOutput = filter === 'activity'
    ? (activityLog && activityLog.length > 0 ? activityLog : ['No AI activity yet. Start an AI meeting to see agent activity here.'])
    : filter !== 'all' && filteredOutput ? filteredOutput : output;
  const terminalRef = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  // Auto-scroll to bottom when new output arrives
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [displayOutput]);

  const handleCopy = async () => {
    const text = stripAnsi(displayOutput.join('\n'));
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy terminal output:', err);
    }
  };

  const errorCount = output.filter((line) =>
    /\[Error\]|error:|ERR!|Error:|failed|FAILED|Failed|Cannot|cannot/i.test(line),
  ).length;

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <div className={styles.header}>
        <span className={styles.title}>
          Terminal
          {filter !== 'all' && (
            <span className={styles.filterBadge}>
              {displayOutput.length} / {output.length}
            </span>
          )}
          {errorCount > 0 && (
            <>
              <span className={styles.errorBadge} title={`${errorCount} errors detected`}>
                {errorCount} errors
              </span>
              {onTryFix && (
                <button
                  className={styles.tryFixBtn}
                  onClick={() => {
                    // Collect error lines for context
                    const errorLines = output.filter((line) =>
                      /\[Error\]|error:|ERR!|Error:|failed|FAILED|Failed|Cannot|cannot/i.test(line),
                    );
                    const errorContext = `Terminal errors (${errorCount}):\n${stripAnsi(errorLines.slice(0, 10).join('\n'))}`;
                    onTryFix(errorContext);
                  }}
                  title="Ask AI to fix these errors"
                >
                  <Wrench size={12} />
                </button>
              )}
            </>
          )}
        </span>
        <div className={styles.actions}>
          {onFilterChange && (
            <div className={styles.filterGroup}>
              <Filter size={12} className={styles.filterIcon} />
              {FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`${styles.filterBtn} ${filter === opt.value ? styles.filterBtnActive : ''}`}
                  onClick={() => onFilterChange(opt.value)}
                  title={opt.title}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          {onClear && (
            <button
              className={styles.actionBtn}
              onClick={onClear}
              title="Clear terminal"
              disabled={output.length === 0}
            >
              <Trash2 size={14} />
            </button>
          )}
          <button
            className={`${styles.actionBtn} ${styles.copyBtn}`}
            onClick={handleCopy}
            title={copied ? 'Copied!' : 'Copy all logs'}
            disabled={displayOutput.length === 0}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>
        </div>
      </div>
      <pre ref={terminalRef} className={styles.terminal}>
        {displayOutput.length === 0 ? (
          <span className={styles.empty}>
            {filter === 'activity'
              ? 'No AI activity yet. Start an AI meeting to see agent activity here.'
              : filter !== 'all'
              ? `No ${filter === 'errors' ? 'errors' : filter === 'warnings' ? 'warnings or errors' : 'installation issues'} found.`
              : 'Terminal output will appear here...'}
          </span>
        ) : (
          stripAnsi(displayOutput.join('\n'))
        )}
      </pre>
    </div>
  );
};
